import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import useGetPendingOrderApprovals from "@features/ecommerce/hooks/useGetPendingOrderApprovals";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { useOrderDrawer } from "@features/orders-features/components/order-drawer";
import { getPendingApprovalLabel } from "@features/orders-features/functions/pendingApprovalLabel";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { getApprovalReviewHref } from "@features/orders-features/components/order-approval-drawer/approvalReviewRoute";
import {
  createSavedCurrencyFormatter,
  getCurrencyDisplay,
} from "shared-ui/src/functions/currency";

const pendingOrdersPath = "/Checkout/WebOrderPending.aspx";
const approvalOrderPath = "/Checkout/WebOrderapprove.aspx";
const baseUrl = getShiComBaseUrl();
const viewAllUrl: string = `${baseUrl}${pendingOrdersPath}`;
const contactAccountTeamUrl = `${baseUrl}/CustomerService/contact-us`;

export default function ActionItemsWidget({
  pageLayoutWidget,
}: BaseWidgetProps) {
  const response = useGetPendingOrderApprovals(5);
  const data = response.data?.items ?? [];
  const { isError, isLoading, isFetching } = response;
  const isDoneLoading = !isLoading && !isFetching && !isError;
  const isEmpty = data.length === 0;
  const { openOrderDrawer, isAvailable } = useOrderDrawer();
  const {
    enabled: orderApprovalsEnabled,
    loading: orderApprovalsLoading,
  } = useFeatureFlag("nomads.order-approvals", false);
  const useInternalApprovalRoute =
    orderApprovalsEnabled && !orderApprovalsLoading;

  return (
    <WidgetWrapper
      pageLayoutWidget={{
        ...pageLayoutWidget,
        widgetGroup: undefined,
      }}
      error={isError}
      cardHeaderDivider
      noPadding
      errorContactAccountTeamLink={contactAccountTeamUrl}
      hideWidgetContentOnError={true}
    >
      <Box sx={{ pb: 1}}>
        <Box>
          {isEmpty && isDoneLoading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={0.75}
              sx={{ py: 4, px: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon
                  data-testid="icon-checkmark"
                  sx={{ color: "action.active", fontSize: 20 }}
                />
                <Typography variant="body1" color="text.secondary">
                  You're all caught up!
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                You currently have no action items.
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ maxHeight: 320, overflowY: "auto", pr: 1 }}>
              <Stack spacing={0} sx={{ pt: 1 }}>
                {data.map((item) => {
                  const pendingLabel = getPendingApprovalLabel(item.daysPending);

                  const orderedBy =
                    item.orderedBy != null ? String(item.orderedBy) : "";
                  const productCount =
                    item.productCount != null ? Number(item.productCount) : 0;
                  const totalPrice =
                    item.totalPrice != null ? Number(item.totalPrice) : 0;
                  const formatPrice = createSavedCurrencyFormatter(item.currency).format;

                  const poNumber =
                    item.poNumber != null ? String(item.poNumber) : "";
                  const webOrderId =
                    item.webOrderId != null ? String(item.webOrderId) : "";

                  const primaryLinkText = item.webOrderId
                    ? `${webOrderId} ${poNumber}`.trim()
                    : `${poNumber}`;

                  const reviewHref = useInternalApprovalRoute && item.webOrderId
                    ? AppendIfPrefixExists(
                        getApprovalReviewHref(item.webOrderId),
                      )
                    : item.reviewUrl || viewAllUrl;

                  return (
                    <Box
                      key={`${poNumber}-${String(item.dateOrdered ?? "")}`}
                      sx={{
                        px: 2,
                        display: "grid",
                        gridTemplateColumns: "24px minmax(0, 1fr) 220px",
                        columnGap: 1.0,
                        alignItems: "center",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        pb: 2,
                        pt: 2,
                      }}
                    >
                      <HowToRegIcon
                        data-testid="icon-approval"
                        color="action"
                        sx={{ fontSize: 20 }}
                      />

                      <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            minWidth: 0,
                            flexWrap: "wrap",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              flexShrink: 0,
                              fontWeight: 500,
                              lineHeight: 1.2,
                            }}
                          >
                            Approval Needed
                          </Typography>

                          <Button
                            variant="text"
                            size="small"
                            {...(!isAvailable && { component: "a", href: reviewHref })}
                            onClick={(e) => {
                              if (!isAvailable) return;
                              e.preventDefault();
                              e.stopPropagation();
                              const salesOrder = webOrderId || poNumber;
                              if (salesOrder) {
                                openOrderDrawer(
                                  {
                                    salesOrder,
                                    webOrderId: item.webOrderId ?? undefined,
                                  },
                                  {
                                    orderDate: item.dateOrdered?.toISOString(),
                                    purchaseOrder: poNumber,
                                    orderedBy: orderedBy,
                                    total: totalPrice,
                                    currencyCode: getCurrencyDisplay(item.currency).code,
                                    totalItems: productCount,
                                  },
                                );
                              }
                            }}
                            sx={{
                              fontWeight: 500,
                              minWidth: 0,
                              px: 0.5,
                              py: 0,
                              ml: 0.5,
                              display: "inline-flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                              overflow: "visible",
                              lineHeight: 1.2,
                              fontSize: "0.875rem",
                              textTransform: "none",
                            }}
                          >
                            {webOrderId ? (
                              <Box component="span" sx={{ flexShrink: 0 }}>
                                {webOrderId}
                              </Box>
                            ) : null}

                            {poNumber ? (
                              <Box component="span" sx={{ flexShrink: 0 }}>
                                {poNumber}
                              </Box>
                            ) : null}

                            {!webOrderId && !poNumber ? primaryLinkText : null}
                          </Button>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "text.secondary",
                            typography: "caption",
                            pt: 0.75,
                            flexWrap: "wrap",
                            whiteSpace: "normal",
                            overflow: "hidden",
                            lineHeight: 1.2,
                          }}
                        >
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            Ordered by {orderedBy}
                          </Box>
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            {productCount} items
                          </Box>
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            {formatPrice(totalPrice)}
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          width: "100%",
                          ml: 0.5,
                          minHeight: 28,
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: pendingLabel?.color,
                            justifySelf: "end",
                            mr: 0.5,
                            whiteSpace: "nowrap",
                            fontSize: 13,
                            lineHeight: 1.2,
                          }}
                          variant="caption"
                        >
                          {pendingLabel?.label}
                        </Typography>

                        {item.canApprove ? (
                          <Button
                            component="a"
                            href={
                              useInternalApprovalRoute && item.webOrderId
                                ? AppendIfPrefixExists(
                                    getApprovalReviewHref(item.webOrderId),
                                  )
                                : `${baseUrl}${approvalOrderPath}?weborderID=${item.webOrderId}`
                            }
                            variant="text"
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              py: 0.25,
                              textTransform: "none",
                            }}
                          >
                            REVIEW
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
                {data.length > 0 && (
                  <Box sx={{ pt : 1, px: 2 }}>
                    <Button
                      aria-label="View all pending approvals"
                      href={
                        useInternalApprovalRoute
                          ? AppendIfPrefixExists(
                              "/orders/management?tab=pendingApprovals",
                            )
                          : viewAllUrl
                      }
                      {...telemetryProps({
                        name: "orders_action_items_view_all_pending_approvals",
                        section: "orders_action_items",
                        label: "View all pending approvals",
                      })}
                     >
                      VIEW ALL
                    </Button>

                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </WidgetWrapper>
  );
}
